import type { EstadoDeSesion } from '../../sesion/ProveedorDeSesion';

/**
 * A dónde hay que llevar a la persona según el estado de su sesión y dónde está parada.
 *
 * Es una función pura porque la regla se rompió en producción y hay que poder probarla sin
 * navegador: el administrador salió parado en `/plataforma`, entró el dueño de un comercio en la
 * misma pestaña, y como la ruta no se movió, vio la pantalla de plataforma —con la lista de
 * comercios que la caché del navegador todavía tenía—. El servidor le negó todo, pero la pantalla
 * ya se había mostrado.
 *
 * Tres reglas:
 *
 * 1. **Quien sale deja la ruta en la puerta.** Sin sesión, la única ruta es `/`: el próximo que
 *    entre arranca desde cero y no desde donde quedó el anterior.
 * 2. **`/plataforma` es sólo de quien administra la instalación.** Cualquier otro que llegue ahí
 *    —escribiendo la ruta, por historial, por herencia de una sesión anterior— va a `/`.
 * 3. **Quien administra y no tiene comercio va a `/plataforma`**, no al alta guiada.
 *
 * Devuelve la ruta a la que hay que ir, o `null` si la persona puede quedarse donde está. Mientras
 * se comprueba la sesión no se mueve a nadie: todavía no se sabe quién es.
 */
export function destinoObligado(situacion: {
  readonly estado: EstadoDeSesion;
  readonly ruta: string;
  readonly administra: boolean;
  readonly tieneAlguna: boolean;
}): string | null {
  const { estado, ruta, administra, tieneAlguna } = situacion;
  if (estado === 'cargando') return null;
  if (estado === 'sin_sesion') return ruta === '/' ? null : '/';
  if (ruta === '/plataforma' && !administra) return '/';
  if (estado === 'sin_empresa' && administra && !tieneAlguna && ruta !== '/plataforma') {
    return '/plataforma';
  }
  return null;
}
